import { createFileRoute, notFound } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import {
  Article,
  ArticleContainer,
  ArticleDescription,
  ArticleHeader,
  ArticleHighlightImage,
  ArticleHeading,
} from "@/components/primitives/article";
import { PageContainer } from "@/components/primitives/layout";
import { SingleBreadcrumb } from "@/components/primitives/single-breadcrumb";
import { renderMdx } from "@/components/content/render-mdx";
import {
  getChangelogEntryBySlug,
  getChangelogMetadata,
} from "@/lib/content/changelog";

const getChangelogEntry = createServerFn({ method: "GET" })
  .inputValidator((data: { slug: string }) => data)
  .handler(async ({ data }) => {
    const entry = getChangelogEntryBySlug(data.slug);
    if (!entry) return null;
    return entry;
  });

export const Route = createFileRoute("/changelog/$slug")({
  loader: async ({ params }) => {
    const entry = await getChangelogEntry({ data: { slug: params.slug } });
    if (!entry) throw notFound();
    return entry;
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    return getChangelogMetadata(loaderData);
  },
  component: ChangelogEntryPage,
});

function ChangelogEntryPage() {
  const entry = Route.useLoaderData();

  return (
    <PageContainer>
      <ArticleContainer>
        <ArticleHeader>
          <SingleBreadcrumb href="/changelog" label="Back to What's New" />
          <ArticleHeading>{entry.title}</ArticleHeading>
          <ArticleDescription>
            {new Date(entry.publishedAt).toLocaleDateString(undefined, {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </ArticleDescription>
        </ArticleHeader>
        {entry.image ? (
          <ArticleHighlightImage src={entry.image} alt={entry.title} />
        ) : null}
        <Article>{renderMdx(entry.body)}</Article>
      </ArticleContainer>
    </PageContainer>
  );
}
